import { useState } from "react";
import { Bike, Printer, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { MTopBar } from "@/components/mobile/MTopBar";
import { MPage, Loading, EmptyArt, Chip, MTabs, ReasonDialog, cardCls, blueBtn } from "@/components/mobile/parts";
import { usePoll } from "@/hooks/useMobileData";
import { useAuth } from "@/context/AuthContext";
import { api, brl, canSee, errMsg, fmtTime, PAY_LABELS } from "@/lib/api";
import { ORDER_STATUS, ORDER_TYPES, nextStatus, sendPrint } from "@/lib/mobile";
import { cn } from "@/lib/utils";

const OPEN = ["recebido", "confirmado", "preparo", "pronto", "entrega"];
const TONE = { recebido: "red", confirmado: "blue", preparo: "amber", pronto: "green", entrega: "blue", entregue: "slate", cancelado: "slate" };

export function DeliveryCard({ o, onChange, approvalMode = false }) {
  const [busy, setBusy] = useState(false);
  const [refuse, setRefuse] = useState(false);
  const { session } = useAuth();
  const next = nextStatus(o, session.tenant.settings.order_steps);
  const move = async (status, reason = "") => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/orders/${o.id}/status`, { status, reason });
      if (o.status === "recebido" && status !== "cancelado") sendPrint("delivery", `Pedido #${data.number} · ${ORDER_TYPES[data.type] || data.type}`, { order: data }).catch(() => {});
      toast.success(`Pedido #${o.number}: ${ORDER_STATUS[status]}`);
      setRefuse(false);
      onChange();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const reprint = () => sendPrint("delivery", `2ª via · Pedido #${o.number}`, { order: o, reprint: true }).then(() => toast.success("Enviado para a impressora do caixa")).catch((e) => toast.error(errMsg(e)));
  return (
    <div className={cn(cardCls, "space-y-2 p-4")} data-testid={`m-delivery-${o.number}`}>
      <div className="flex items-start gap-2">
        <div className="flex-1">
          <p className="font-semibold">#{o.number} · {ORDER_TYPES[o.type] || o.type}{o.table_number ? ` ${o.table_number}` : ""}{o.comanda_number ? ` ${o.comanda_number}` : ""}</p>
          <p className="text-xs text-slate-500">{fmtTime(o.created_at)} · {o.customer?.name}{o.customer?.phone ? ` · ${o.customer.phone}` : ""}</p>
        </div>
        <Chip tone={TONE[o.status]}>{ORDER_STATUS[o.status]}</Chip>
      </div>
      {o.type === "delivery" && o.customer?.address && <p className="text-xs text-slate-600">{o.customer.address}</p>}
      <p className="text-sm text-slate-700">{o.items.map((i) => `${i.qty}x ${i.name}`).join(", ")}</p>
      <div className="flex items-center justify-between text-sm"><span className="text-slate-500">{PAY_LABELS[o.payment_method] || o.payment_method}</span><span className="font-mono font-bold">{brl(o.total)}</span></div>
      {OPEN.includes(o.status) && (
        <div className="flex gap-2 pt-1">
          {o.status === "recebido" && <Button variant="outline" className="h-11 flex-1 text-red-600" disabled={busy} onClick={() => setRefuse(true)} data-testid={`m-delivery-refuse-${o.number}`}>Recusar</Button>}
          {!approvalMode && <Button variant="outline" className="h-11" onClick={reprint} aria-label="Reimprimir" data-testid={`m-delivery-reprint-${o.number}`}><Printer className="h-4 w-4" /></Button>}
          {next && (!approvalMode || o.status === "recebido") && (
            <Button className={cn("h-11 flex-1", blueBtn)} disabled={busy} onClick={() => move(next[0])} data-testid={`m-delivery-next-${o.number}`}>{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{approvalMode ? "Aprovar e enviar à cozinha" : next[1]}</Button>
          )}
        </div>
      )}
      <ReasonDialog open={refuse} title={`Recusar o pedido #${o.number}?`} confirmText="Recusar pedido" onClose={() => setRefuse(false)} onConfirm={(r) => move("cancelado", r)} testId={`m-refuse-${o.number}`} />
    </div>
  );
}

export default function MDelivery() {
  const { session } = useAuth();
  const allowed = canSee(session.user, "delivery");
  const [tab, setTab] = useState("open");
  const { data, load } = usePoll(() => (allowed ? api.get("/orders").then((r) => r.data) : Promise.resolve([])), 10000, [allowed]);
  if (!allowed) return <><MTopBar title="Delivery" /><EmptyArt icon={Bike} title="Sem acesso ao delivery" /></>;
  const list = (data || []).filter((o) => (tab === "open" ? OPEN.includes(o.status) : !OPEN.includes(o.status)));
  const openCount = (data || []).filter((o) => OPEN.includes(o.status)).length;
  return (
    <>
      <MTopBar title="Delivery" />
      <MPage>
        <MTabs value={tab} onChange={setTab} items={[["open", "Em aberto", openCount], ["done", "Finalizados hoje"]]} testId="m-delivery-tabs" />
        {!data ? <Loading /> : !list.length ? <EmptyArt icon={Bike} title={tab === "open" ? "Nenhum pedido em aberto" : "Nenhum pedido finalizado hoje"} testId="m-delivery-empty" />
          : <div className="space-y-2.5">{list.map((o) => <DeliveryCard key={o.id} o={o} onChange={load} />)}</div>}
      </MPage>
    </>
  );
}
